<template>
  <div class="turnstile-widget">
    <div ref="container" />
    <p v-if="failed" class="turnstile-error">
      Verification could not load. Please refresh the page and try again.
    </p>
  </div>
</template>

<script>
// Cloudflare Turnstile bot-protection widget for the public claim form. Loads
// the Turnstile script once (shared across instances), renders an explicit
// "managed" widget, and surfaces the resulting token to the parent via the
// `verified` event.
//
// IMPORTANT: the token is only a client-side signal. The parent sends it to the
// backend verify-gate endpoint, which verifies it against the Turnstile
// siteverify API (https://challenges.cloudflare.com/turnstile/v0/siteverify)
// before letting the claim form render. The token is single-use and is not
// reused at submit.
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

// One shared loader promise so mounting several widgets never injects the
// script more than once.
let scriptPromise = null

function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve()
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => {
      // let a later mount retry the injection
      scriptPromise = null
      reject(new Error('Failed to load Turnstile'))
    }
    document.head.appendChild(script)
  })
  return scriptPromise
}

export default {
  name: 'TurnstileWidget',
  emits: ['verified', 'expired', 'error'],
  props: {
    // Public site key. Configured per environment via VUE_APP_TURNSTILE_SITE_KEY.
    siteKey: {
      type: String,
      default: () => process.env.VUE_APP_TURNSTILE_SITE_KEY || '',
    },
    // Optional label surfaced in the Turnstile analytics dashboard.
    action: {
      type: String,
      default: 'claim-submit',
    },
  },
  data() {
    return {
      widgetId: null,
      failed: false,
    }
  },
  async mounted() {
    if (!this.siteKey) {
      // Fail closed but visibly: without a site key the widget cannot issue a
      // token, so the parent's submit gate stays locked. Set
      // VUE_APP_TURNSTILE_SITE_KEY for this environment.
      this.failed = true
      this.$emit('error')
      // eslint-disable-next-line no-console
      console.error('[Turnstile] VUE_APP_TURNSTILE_SITE_KEY is not set')
      return
    }
    try {
      await loadTurnstileScript()
      this.renderWidget()
    } catch (e) {
      // the script failed to load or render; fail closed and surface it
      this.failed = true
      this.$emit('error')
      // eslint-disable-next-line no-console
      console.error('[Turnstile] failed to load', e)
    }
  },
  beforeDestroy() {
    if (this.widgetId && window.turnstile) {
      window.turnstile.remove(this.widgetId)
    }
  },
  methods: {
    renderWidget() {
      this.widgetId = window.turnstile.render(this.$refs.container, {
        sitekey: this.siteKey,
        action: this.action,
        theme: 'light',
        callback: (token) => {
          this.failed = false
          this.$emit('verified', token)
        },
        'expired-callback': () => {
          // tokens live ~300s; drop the stale one and fetch a fresh one so a
          // user lingering on the review step can still submit
          this.$emit('expired')
          this.reset()
        },
        'error-callback': () => {
          this.failed = true
          this.$emit('error')
        },
      })
    },
    reset() {
      if (this.widgetId && window.turnstile) {
        window.turnstile.reset(this.widgetId)
      }
    },
  },
}
</script>

<style scoped>
.turnstile-widget {
  margin-top: 12px;
}

.turnstile-error {
  margin: 8px 0 0;
  color: #c0392b;
  font-size: 12px;
  font-weight: 700;
}
</style>
